// Pojedyncze źródło prawdy dla linków CTA prowadzących do /search.
//
// Zasady (kontrakt UX uzgodniony z testami search-roundtrip-dates +
// seo-page-view-single-form):
//
//   • Każdy link CTA przekazuje DOKŁADNIE: from, to, dd, rd, adults.
//   • Żadnych parametrów "redundantnych" (ot=RoundTrip, c=0, i=0,
//     sc=economy, d=0) — są zgodne z defaultami `searchSchema` w
//     src/routes/search.tsx i ich obecność w URL fałszywie sugerowała
//     "user coś tu wybrał", co blokowało 301 → /loty/$from/$to oraz
//     powodowało, że SearchForm po stronie /search rehydratował się
//     z innym zestawem propsów (vide: "drugi formularz").
//   • Dokładnie jedno wystąpienie każdego klucza w query — pilnowane
//     przez URLSearchParams.
//
// Jeśli w przyszłości pojawi się potrzeba CTA z innymi defaultami
// (np. business class), dodaj nowy, NAZWANY helper obok — nie rozdmuchuj
// `buildSearchHref` opcjonalnymi flagami, bo natychmiast przestanie
// "zawsze przekazywać dokładnie dd/rd/adults".

export interface SearchHrefInput {
  /** Kod IATA lotniska wylotu (3 litery, np. "WAW"). */
  from: string;
  /** Kod IATA lotniska docelowego (3 litery, np. "BCN"). */
  to: string;
  /** Data wylotu w formacie ISO YYYY-MM-DD. */
  dd: string;
  /** Data powrotu w formacie ISO YYYY-MM-DD. Pomijana, gdy `undefined`. */
  rd?: string;
  /** Liczba dorosłych pasażerów (1–9). Domyślnie 1. */
  adults?: number;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const IATA_RE = /^[A-Z]{3}$/;

function assertInput(input: SearchHrefInput): void {
  if (!IATA_RE.test(input.from)) {
    throw new Error(`buildSearchHref: invalid IATA 'from'=${JSON.stringify(input.from)}`);
  }
  if (!IATA_RE.test(input.to)) {
    throw new Error(`buildSearchHref: invalid IATA 'to'=${JSON.stringify(input.to)}`);
  }
  if (!ISO_RE.test(input.dd)) {
    throw new Error(`buildSearchHref: invalid ISO 'dd'=${JSON.stringify(input.dd)}`);
  }
  if (input.rd !== undefined && !ISO_RE.test(input.rd)) {
    throw new Error(`buildSearchHref: invalid ISO 'rd'=${JSON.stringify(input.rd)}`);
  }
  if (input.adults !== undefined) {
    if (!Number.isInteger(input.adults) || input.adults < 1 || input.adults > 9) {
      throw new Error(`buildSearchHref: 'adults' must be int 1–9, got ${input.adults}`);
    }
  }
}

/**
 * Buduje canonical link CTA do /search. Stałe pola: from, to, dd, a (adults).
 * Pole `rd` dołączane tylko jeśli przekazane (RoundTrip).
 *
 * Kolejność kluczy jest deterministyczna (stabilne URL-e ułatwiają
 * cache-hit, dedup w analytics i porównywanie w testach).
 */
export function buildSearchHref(input: SearchHrefInput): string {
  assertInput(input);
  const adults = input.adults ?? 1;
  const params = new URLSearchParams();
  params.set("from", input.from);
  params.set("to", input.to);
  params.set("dd", input.dd);
  if (input.rd) params.set("rd", input.rd);
  params.set("a", String(adults));
  // Parametry odczytywane przez wyszukiwarkę Travelpayouts (prefill + auto-wyszukanie).
  const firstDate = (v: string) => (v.match(/\d{4}-\d{2}-\d{2}/)?.[0] ?? "");
  params.set("origin", input.from);
  params.set("destination", input.to);
  const dep = firstDate(input.dd);
  if (dep) params.set("depart_date", dep);
  const ret = input.rd ? firstDate(input.rd) : "";
  if (ret) params.set("return_date", ret);
  params.set("adults", String(adults));
  return `/search?${params.toString()}`;
}
